import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import {
  api,
  clearSession,
  loadSession,
  loadShownFrame,
  saveSession,
  saveShownFrame,
  type StoredSession,
} from './lib/api';
import { go, useRoute } from './lib/route';
import { isViewerHost, isViewerRoute } from './lib/viewer';
import { Account } from './pages/Account';
import { Album } from './pages/Album';
import { Billing } from './pages/Billing';
import { Albums } from './pages/Albums';
import { Device } from './pages/Device';
import { Devices } from './pages/Devices';
import { Join } from './pages/Join';
import { PublicUpload } from './pages/PublicUpload';
import { SignIn } from './pages/SignIn';

/**
 * The viewer drags in the slideshow, its stylesheet and the pairing loop. None
 * of that belongs in the bundle someone opens to send three photos, so it is
 * fetched only when a screen actually asks to be a frame.
 */
const Viewer = lazy(() =>
  import('./pages/Viewer').then((module) => ({ default: module.Viewer })),
);

export const App = () => {
  const [session, setSession] = useState<StoredSession | null>(loadSession);
  const [checked, setChecked] = useState(false);
  /** Which frame the viewer shows when it is showing one as this account. */
  const [frameId, setFrameId] = useState<string | null>(loadShownFrame);
  const route = useRoute();

  // The viewer is a hostname or a path rather than one of the app's own routes,
  // so it is read separately from `useRoute` — but it still has to be recomputed
  // when the hash moves, because `#/viewer` is one of the ways in.
  const [viewer, setViewer] = useState(isViewerRoute);
  useEffect(() => {
    const onHashChange = () => setViewer(isViewerRoute());
    window.addEventListener('hashchange', onHashChange);
    return () => window.removeEventListener('hashchange', onHashChange);
  }, []);

  // A stored token may have been revoked from another device; find out before
  // showing an interface that is going to 401 on first use.
  //
  // The viewer is no longer skipped: on the app's own origin it may be showing
  // a frame on this account's authority, and it wants to know as much as any
  // other page. On `viewer.*` there is no session to load in the first place,
  // because `localStorage` is per origin, so this costs that hostname nothing.
  useEffect(() => {
    const stored = loadSession();
    if (!stored) {
      setChecked(true);
      return;
    }
    let live = true;
    api
      .whoAmI(stored.token)
      .then((who) => {
        if (!live) return;
        const next = { token: stored.token, account: who.account };
        saveSession(next);
        setSession(next);
      })
      .catch(() => {
        if (!live) return;
        clearSession();
        setSession(null);
      })
      .finally(() => live && setChecked(true));
    return () => {
      live = false;
    };
  }, []);

  /**
   * Signed in with no frame chosen: take the first one this account can see.
   *
   * Newest first is what the API returns, and the newest frame is very often
   * the one somebody has just made and is standing in front of. It is a guess,
   * but a guess in the direction of the viewer simply working, and the Screen
   * panel's switcher is one press away when it is wrong.
   */
  useEffect(() => {
    if (!viewer || !session || frameId !== null) return;
    let live = true;
    api
      .listDevices(session.token)
      .then((result) => {
        const first = result.devices[0];
        if (!live || !first) return;
        setFrameId(first.id);
        saveShownFrame(first.id);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [frameId, session, viewer]);

  const chooseFrame = useCallback((id: string) => {
    setFrameId(id);
    saveShownFrame(id);
  }, []);

  const onSignedIn = useCallback((next: StoredSession) => {
    saveSession(next);
    setSession(next);
  }, []);

  const signOut = useCallback(() => {
    const current = loadSession();
    // Fire and forget: the session is gone locally either way, and a failed
    // revoke should not leave someone stuck on a page they wanted to leave.
    if (current) void api.signOut(current.token).catch(() => undefined);
    clearSession();
    setSession(null);
    setFrameId(null);
    window.location.hash = '';
  }, []);

  if (viewer) {
    return (
      // Black from the first paint: this fallback is what a cold load shows
      // while the viewer chunk arrives.
      <Suspense fallback={<div className="frame-root" />}>
        <Viewer
          session={session}
          frameId={frameId}
          // Withheld on `viewer.*`. That hostname can never hold a session, so
          // offering to sign in or switch frames there would be an interface
          // for something that cannot happen — and the one-app-hostname rule in
          // the README is what keeps it that way.
          {...(isViewerHost() ? {} : { onChooseFrame: chooseFrame })}
        />
      </Suspense>
    );
  }

  // The public upload page is the other route that never wants an account. It
  // has to come before the sign-in gate or a stranger with a link hits a wall.
  if (route.kind === 'frame') return <PublicUpload slug={route.slug} />;

  if (!checked) {
    return (
      <main className="shell">
        <p className="eyebrow">Loading</p>
      </main>
    );
  }

  if (!session) {
    return (
      <SignIn
        mode={route.kind === 'signup' ? 'signup' : 'signin'}
        // Someone following an invite link needs to know what they are signing
        // up *for* before they are asked for an email address.
        invite={route.kind === 'join' ? route.invite : null}
        onSignedIn={onSignedIn}
      />
    );
  }

  if (route.kind === 'join') {
    return <Join session={session} invite={route.invite} />;
  }
  if (route.kind === 'device') {
    return <Device session={session} deviceId={route.id} tab={route.tab} onSignOut={signOut} />;
  }
  if (route.kind === 'albums') {
    return <Albums session={session} onSignOut={signOut} />;
  }
  if (route.kind === 'album') {
    return <Album session={session} albumId={route.id} onSignOut={signOut} />;
  }
  if (route.kind === 'account') {
    return <Account session={session} onSignOut={signOut} onUpdated={onSignedIn} />;
  }
  if (route.kind === 'billing') {
    return <Billing session={session} onSignOut={signOut} checkout={route.checkout} />;
  }
  return (
    <Devices
      session={session}
      onSignOut={signOut}
      onShowHere={(id) => {
        chooseFrame(id);
        go('/viewer');
      }}
    />
  );
};
